import React, { memo, useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';

import Icon from '../Icon';
import CarouselSlide from './CarouselSlide';
import CarouselNav from './CarouselNav';
import CarouselFullscreen from './CarouselFullscreen';

import styles from './styles';

const useStyles = createUseStyles(styles, 'Carousel');

const Carousel = ({
    classes: classesProp,
    className: classNameProp,
    currentIndex: currentIndexProp,
    embedded = false,
    images = [],
    minVersion = false,
    onIndexChange,
    overrides: overridesProp,
    placeholder,
}) => {
    const classes = useClasses(useStyles, classesProp);
    const override = getOverrides(overridesProp, Carousel.overrides);

    const [internalIndex, setInternalIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const isControlled = currentIndexProp !== undefined && currentIndexProp !== null;
    const currentIndex = isControlled ? currentIndexProp : internalIndex;

    const imagesCount = images?.length || 0;
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < imagesCount - 1;
    const showNav = imagesCount > 1 && (embedded || !minVersion || isFullscreen);

    const rootClass = classnames(classNameProp, classes.root, {
        [classes.minimalist]: minVersion && !embedded,
        [classes.embedded]: embedded,
    });

    const slidesStyle = useMemo(
        () => ({
            transform: `translateX(-${currentIndex * 100}%)`,
        }),
        [currentIndex],
    );

    const setCurrentIndex = useCallback(
        (nextIndex) => {
            const resolvedIndex =
                typeof nextIndex === 'function'
                    ? nextIndex(isControlled ? currentIndexProp : internalIndex)
                    : nextIndex;

            if (!isControlled) {
                setInternalIndex(resolvedIndex);
            }

            onIndexChange?.(resolvedIndex);
        },
        [currentIndexProp, internalIndex, isControlled, onIndexChange],
    );

    const handlePrevious = useCallback(() => {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
    }, [setCurrentIndex]);

    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => Math.min(imagesCount - 1, prev + 1));
    }, [imagesCount, setCurrentIndex]);

    const handleSelectSlide = useCallback(
        (index) => {
            setCurrentIndex(index);
        },
        [setCurrentIndex],
    );

    const handleOpenFullscreen = useCallback(() => {
        setIsFullscreen(true);
    }, []);

    const handleCloseFullscreen = useCallback(() => {
        setIsFullscreen(false);
    }, []);

    const handleContainerClick = useCallback(() => {
        if (!embedded && minVersion && !isFullscreen) {
            handleOpenFullscreen();
        }
    }, [embedded, minVersion, isFullscreen, handleOpenFullscreen]);

    if (!images || imagesCount === 0) return null;

    return (
        <div className={rootClass} onClick={handleContainerClick} {...override.root}>
            <div className={classes.slideWrapper}>
                <div className={classes.slidesTrack} style={slidesStyle}>
                    {images.map((image, index) => (
                        <CarouselSlide
                            key={index}
                            image={image}
                            placeholder={placeholder}
                            isActive={index === currentIndex}
                            classes={classes}
                        />
                    ))}
                </div>

                {showNav && (
                    <>
                        <CarouselNav
                            direction="left"
                            disabled={!hasPrevious}
                            onClick={handlePrevious}
                            classes={classes}
                        />
                        <CarouselNav
                            direction="right"
                            disabled={!hasNext}
                            onClick={handleNext}
                            classes={classes}
                        />
                    </>
                )}

                {!embedded && !minVersion && (
                    <button
                        type="button"
                        className={classes.fullscreenButton}
                        onClick={handleOpenFullscreen}
                    >
                        <Icon name="fullscreen" size="large" color="white" />
                    </button>
                )}
            </div>

            {!embedded && isFullscreen && (
                <CarouselFullscreen
                    images={images}
                    currentIndex={currentIndex}
                    placeholder={placeholder}
                    onClose={handleCloseFullscreen}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onSelectSlide={handleSelectSlide}
                    classes={classes}
                    {...override.Fullscreen}
                />
            )}
        </div>
    );
};

Carousel.overrides = ['root', 'Fullscreen'];

Carousel.propTypes = {
    /** Custom CSS class for styling the carousel container */
    className: PropTypes.string,
    /** Object with custom style classes for inner elements */
    classes: PropTypes.object,
    /** Array of image objects to display in the carousel */
    images: PropTypes.arrayOf(
        PropTypes.shape({
            /** URL for the full-size image */
            original: PropTypes.string,
            /** URL for the thumbnail image, used in fullscreen mode */
            thumbnail: PropTypes.string,
        }),
    ),
    /** When true, shows a simplified version with hidden navigation until fullscreen mode is activated */
    minVersion: PropTypes.bool,
    /** Notifies parent when the active slide changes */
    onIndexChange: PropTypes.func,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** Image URL to display when an image fails to load */
    placeholder: PropTypes.string,
    /** Controlled active slide index */
    currentIndex: PropTypes.number,
    /** Renders inline carousel without fullscreen affordances (for external modal hosts) */
    embedded: PropTypes.bool,
};

export default memo(Carousel);
